import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { message as antMessage } from 'antd';
import {
  getBetSlipFromLs,
  getType,
  removeAllBetSlipEvent,
  removeBetSlipEvent
} from 'utils/betslip/betslip';
import LS from 'utils/bom-dom-manipulation/local-storage';
import {
  BET_SP_COEFICIENT,
  BET_SUCCESS_MODAL_DURATION,
  COUNTER_OFFER_ACTIVE_TYPE,
  SUPER_BET_ACTIVE_TYPE
} from 'utils/constants/app/magic-numbers-app';
import {
  BET_TYPE_NAMES_KEYS,
  BET_TYPES,
  EDIT_BET_STATUSES_TO_REMOVE_EVENT_FROM_BETSLIP
} from 'utils/constants/betslip/bet-types';
import { ERROR_CODE_TIMEOUT_1800 } from 'utils/constants/dateInfo/time-numbers';
import { backendErrorCodes } from 'utils/constants/swarm/backend-error-codes';
import { BackendStatusCodes } from 'utils/constants/swarm/backend-status-codes';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { showToastError } from 'utils/generic/show-toast-error';
import { storageKeyName } from 'utils/generic/storage-key-name';
import { isMobile } from 'utils/is-mobile';
import { customizeMessage } from 'utils/swarm/swarm-error-handler';
import {
  BetSlipEventObj,
  EAdvancedSelectedTabs,
  UkBets
} from 'interfaces/bet-data';
import {
  doBetProps,
  EAfterSuccessfulBet,
  EBetslipBetType,
  EBetslipType,
  TSuperBetEventType
} from 'interfaces/betslip';
import { doBet } from 'services/bet-slip';
import { clearEditBet } from 'newelements/_Betslip/layouts/Basic/managers/EditBetManager/editBetActions';
import {
  calculateAndDispatchSingleBetsUpdates,
  calculateAndDispatchUpdates
} from 'newelements/_Betslip/utils/action-middlewares/store-actions-middlewares/calculateAndDispatchUpdates';
import { BETSLIP_DO_BET_CONSTANTS } from 'newelements/_Betslip/utils/constants/doBet';
import { GENERAL_FIELD_KEY } from 'newelements/_Betslip/utils/constants/shared';
import { getIsMultipleGeneralField } from 'newelements/_Betslip/utils/generic/getAdvancedMultipleGeneralField';
import { useSuperBetAvailable } from 'hooks/betslip/useSuperBetAvailable';
import { usePredefinedStakes } from 'hooks/usePredefinedStakes';
import { UseCheckGeoComply } from 'newhooks/account/useCheckGeoComply';
import { batchActions, setCounterOffersUnderReviewModal } from 'store/actions';
import {
  removeFreeBetItem,
  removeProfitBoostItem,
  setBetslipOpen,
  setBetUkType,
  setIsBetInProgress,
  setOpenBetsLoading,
  setSelectedProfitBoostEventId,
  setSentCounterOffersCount,
  setSentSuperBetCount,
  triggerBetted
} from 'store/actions/betslip';
import {
  getAdvancedSelectedTab,
  getAllLsBets,
  getAllStakes,
  getBetsCalculations,
  getBetslipType,
  getBetTypesConf,
  getBookBetId,
  getComputedBetType,
  getCounterOfferActiveEventIds,
  getEditBet,
  getIsFreeBetEnabled,
  getOddsCounterOffers,
  getSelectedFreeBetAmount,
  getSelectedProfitBoostItem,
  getSelectedSystem,
  getSentCounterOffersCount,
  getSentSuperBetCount,
  getSuperBetActiveEventIds
} from 'store/selectors/betslip';
import { getFreeBets } from 'store/selectors/betslip/freeBet';
import { getPartnerConfigs } from 'store/selectors/socket';
/* eslint-disable no-magic-numbers */

// @Todo betslip-types move into betslip types
type TBetObject = {
  amount: number;
  bets: Array<{
    event_id: number;
    price: number;
  }>;
  type: number;
  mode: number;
  source: string;
  bonus_id?: number;
  sys_bet?: number;
  booking_id?: number;
  each_way?: boolean;
};

type Props = {
  currentType: EBetslipBetType;
  setAcceptBet: (value: ((prevState: boolean) => boolean) | boolean) => void;
  setSelected: (data: string) => void;
  toggleBetslipView: (open: boolean) => void;
  betslipRef: { current: HTMLElement | null | undefined };
  totalSingleWin: number;
  totalStake: number;
  setShouldValidateRecaptchaV3: (
    value: ((prevState: boolean) => boolean) | boolean
  ) => void;
};

type UseDoBetType = {
  clearUkTypes: () => void;
  onPlaceBet: () => void;
};

type TSuperBetError = {
  result?: number;
  result_text?: string;
  StatusCode?: string;
};

type TDoBetErrorCallback = {
  data: {
    result: number;
  };
  StatusCode: string;
  details?: Record<string, string>;
  result_text?: string;
  result?: number;
};

export const useDoBet = ({
  currentType,
  setAcceptBet,
  setSelected,
  toggleBetslipView,
  betslipRef,
  totalSingleWin,
  totalStake,
  setShouldValidateRecaptchaV3
}: Props): UseDoBetType => {
  const { predefinedStakes } = usePredefinedStakes();
  const stakes = useSelector(getAllStakes);
  const selectedProfitBoostItem = useSelector(getSelectedProfitBoostItem);
  const calculations = useSelector(getBetsCalculations);

  const dispatch = useDispatch();

  const partnerConfigs = useSelector(getPartnerConfigs);
  const system = useSelector(getSelectedSystem);
  const allLsBets = useSelector(getAllLsBets);
  const editBet = useSelector(getEditBet);
  const betTypesConf = useSelector(getBetTypesConf);
  const betslipType = useSelector(getBetslipType);
  const sentCounterOffersCount = useSelector(getSentCounterOffersCount);
  const sentSuperBetCount = useSelector(getSentSuperBetCount);
  const oddsOffers = useSelector(getOddsCounterOffers);
  const bookBetId = useSelector(getBookBetId);
  const advancedSelectedTab = useSelector(getAdvancedSelectedTab);
  const betslipBetType = useSelector(getComputedBetType);

  const { singleEventsCalculations, general } =
    useSelector(getBetsCalculations);

  const superBetActiveEventIds = useSelector(getSuperBetActiveEventIds);
  const counterOfferActiveEventIds = useSelector(getCounterOfferActiveEventIds);
  const { selectedFreeBetEventId, selectedFreeBetId } =
    useSelector(getFreeBets);

  const isFreeBetEnabled = useSelector(getIsFreeBetEnabled);
  const selectedFreeBetAmount = useSelector(getSelectedFreeBetAmount);

  const { t } = useTranslation();

  const isSuperBetAvailable = useSuperBetAvailable();
  const { doBetHandler } = useDoBetHandler();

  const setInProgress = (data: boolean) => {
    dispatch(setIsBetInProgress(data));
  };

  const removeActiveTabLoading = () => {
    dispatch(setOpenBetsLoading(true));
  };

  const handleSuperBetError = (
    eventId: TSuperBetEventType,
    error: TSuperBetError
  ) => {
    if (error.result === BackendStatusCodes.betslip.useSuperBet) {
      const errorMessage = backendErrorCodes[error.result]
        ? t(backendErrorCodes[error.result])
        : error.result_text || error.StatusCode || '';

      const message = customizeMessage(errorMessage, error);

      if (message) {
        const content = `${message} ${
          isSuperBetAvailable
            ? `<span style="text-decoration: underline">${t(
                'swarm.useSuperBet'
              )}</span>`
            : ''
        }`;

        showToastError(
          content,
          null,
          null,
          () => {
            antMessage.destroy(content);

            if (!superBetActiveEventIds.some(id => id == eventId)) {
              calculateAndDispatchUpdates({ id: eventId, superBet: true });
            }

            if (isMobile()) {
              dispatch(setBetslipOpen(true));
            }
          },
          { cursor: 'pointer' }
        );
      }
    }
  };

  const getBetObj = (
    bets: Array<Partial<BetSlipEventObj>>,
    betType: number,
    each_way?: boolean
  ) => {
    const sendTag = bets.every(({ tag }) => !!tag);
    const firstEventId = bets[0].eventId;

    const mode = (() => {
      // this is for single do bet and it's temporary
      const isCounterOfferActive =
        counterOfferActiveEventIds.some(id => id == firstEventId) &&
        bets.length === 1;

      const isSuperBetActive = superBetActiveEventIds.some(
        id => firstEventId == id || id === GENERAL_FIELD_KEY
      );

      const isSuperBetAllowForPrematch =
        SpringConfigs.SUPER_BET_DEFAULT_ON &&
        bets.every(({ isLive }) => !isLive);

      const isSuperBetActiveForLive =
        SpringConfigs.SUPER_BET_DEFAULT_ON &&
        partnerConfigs?.is_superbet_on_live;

      const shouldBetWithSuperBet =
        isSuperBetActive ||
        isSuperBetAllowForPrematch ||
        isSuperBetActiveForLive;

      if (isCounterOfferActive) {
        return COUNTER_OFFER_ACTIVE_TYPE;
      }

      if (shouldBetWithSuperBet) {
        return SUPER_BET_ACTIVE_TYPE;
      }

      return Number(
        JSON.parse(LS.getItem(storageKeyName('betslip', 'ACCEPT_BET')))?.value
      );
    })();

    const amount = (() => {
      const isFreeBet =
        isFreeBetEnabled && selectedFreeBetId && selectedFreeBetAmount;

      if (isFreeBet) {
        return selectedFreeBetAmount;
      }

      if (currentType !== EBetslipBetType.SINGLE) {
        return general?.finalStake || 0;
      }

      return (
        (bets[0].eventId &&
          singleEventsCalculations[bets[0].eventId]?.finalStake) ||
        0
      );
    })();

    const betEvents = bets.map(bet => ({
      event_id: Number(bet.eventId),
      price: bet.sp ? BET_SP_COEFICIENT : Number(bet.coeficient),
      ...(sendTag ? { tag: bet.tag } : {}),
      booking_id: bet.booking_id
    }));

    const betObj: TBetObject = {
      amount,
      bets: betEvents,
      type: betType,
      mode,
      source: String(SpringConfigs.SOURCE)
    };

    if (isFreeBetEnabled && selectedFreeBetId) {
      betObj.bonus_id = selectedFreeBetId;
    }

    if (betType === BET_TYPES.SYSTEM) {
      betObj.sys_bet = system?.value;
    }

    if (each_way) {
      betObj.each_way = each_way;
    }

    if (bookBetId) {
      betObj.booking_id = Number(bookBetId);
    }

    if (
      selectedProfitBoostItem &&
      (betType === BET_TYPES.SINGLE || betType === BET_TYPES.MULTIPLE)
    ) {
      betObj.bonus_id = selectedProfitBoostItem.id;
    }

    return betObj;
  };

  const singleDoBet = () => {
    allLsBets.forEach(bet => {
      if (selectedFreeBetEventId && bet.eventId !== selectedFreeBetEventId) {
        return;
      }

      if (
        (singleEventsCalculations[bet.eventId]?.finalStake ??
          predefinedStakes.default_stake) ||
        editBet?.amount ||
        selectedFreeBetEventId
      ) {
        const isCounterOfferActive =
          singleEventsCalculations[bet.eventId] &&
          'oddOffer' in singleEventsCalculations[bet.eventId];

        const data = {
          ...getBetObj(
            [
              {
                eventId: Number(bet.eventId),
                coeficient: bet.sp
                  ? BET_SP_COEFICIENT
                  : isCounterOfferActive
                  ? oddsOffers[`odd${bet.eventId}`]
                  : Number(bet.coeficient),
                tag: bet.tag,
                isLive: bet.isLive,
                booking_id: bet.booking_id
              }
            ],
            BET_TYPES.SINGLE
          ),
          each_way: bet.each_way
        };

        const possibleWin =
          calculations.singleEventsCalculations?.[bet.eventId]?.finalResult
            ?.possibleWin || 0;

        doBetHandler(
          data,
          possibleWin,
          (responce: any) => {
            const { result } = responce || {};
            setInProgress(false);

            if (result === 'OK') {
              removeActiveTabLoading();
              setTimeout(() => {
                if (
                  SpringConfigs.AFTER_SUCCESSFUL_BET ===
                  EAfterSuccessfulBet.REMOVE_EVENTS
                ) {
                  removeBetSlipEvent(bet.eventId);
                }

                dispatch(triggerBetted());
              }, BET_SUCCESS_MODAL_DURATION);

              if (selectedProfitBoostItem) {
                dispatch(
                  batchActions(
                    removeProfitBoostItem({
                      id: data.bonus_id,
                      eventId: bet.eventId
                    }),
                    setSelectedProfitBoostEventId(null)
                  )
                );
              }

              if (isFreeBetEnabled && selectedFreeBetId) {
                dispatch(removeFreeBetItem(selectedFreeBetId));
              }

              LS.removeItem(storageKeyName('betslip', 'EDIT_BET_ACTIVE'));

              if (getBetSlipFromLs().bets.length === 0) {
                toggleBetslipView(false);
              }

              clearEditBet();

              if (responce.details?.is_superbet) {
                if (isCounterOfferActive) {
                  dispatch(
                    setSentCounterOffersCount(sentCounterOffersCount + 1)
                  );
                  dispatch(setCounterOffersUnderReviewModal(true));
                } else {
                  dispatch(setSentSuperBetCount(sentSuperBetCount + 1));
                }
              }

              calculateAndDispatchUpdates({
                id: GENERAL_FIELD_KEY,
                stake: ''
              });
              calculateAndDispatchUpdates({
                id: bet.eventId,
                stake: ''
              });
            }
          },
          (e: TDoBetErrorCallback) => {
            setInProgress(false);

            if (e.data && e.data.result === ERROR_CODE_TIMEOUT_1800) {
              setAcceptBet(true);
            }

            handleSuperBetError(bet.eventId, e);

            if (
              e?.StatusCode &&
              SpringConfigs.REMOVE_SUSPENDED_SELECTION &&
              EDIT_BET_STATUSES_TO_REMOVE_EVENT_FROM_BETSLIP.includes(
                e.StatusCode
              )
            ) {
              removeAllBetSlipEvent();
            }
          },
          setAcceptBet,
          document.getElementById('betslip-wrapper') || betslipRef.current
        );
      }
    });
  };

  const multipleAndSystemDoBet = (betType: number) => {
    if (Number(general?.finalStake) || selectedFreeBetId || editBet?.amount) {
      const each_way = !!general?.isEachWay;

      const possibleWin = calculations?.general?.finalResult?.possibleWin || 0;

      doBetHandler(
        getBetObj(allLsBets, betType, each_way),
        possibleWin,
        (responce: any) => {
          const { result } = responce;
          setInProgress(false);

          if (result === 'OK') {
            removeActiveTabLoading();
            setTimeout(() => {
              if (
                SpringConfigs.AFTER_SUCCESSFUL_BET ===
                EAfterSuccessfulBet.REMOVE_EVENTS
              ) {
                removeAllBetSlipEvent();
              }

              clearEditBet();
              dispatch(triggerBetted());
              // modal success for displaying
            }, BET_SUCCESS_MODAL_DURATION);

            LS.removeItem(storageKeyName('betslip', 'EDIT_BET_ACTIVE'));

            if (responce.details?.is_superbet) {
              // at the moment we can only do bet with counter offer using single type, but in future we should add opportunity also for multiple and system
              // if (isCounterOfferActive) {
              //   dispatch(setSentCounterOffersCount(sentCounterOffersCount + 1));
              // } else {
              dispatch(setSentSuperBetCount(sentSuperBetCount + 1));
              // }
            }

            if (isFreeBetEnabled && selectedFreeBetId) {
              dispatch(removeFreeBetItem(selectedFreeBetId));
            }

            if (selectedProfitBoostItem) {
              dispatch(
                batchActions(
                  removeProfitBoostItem({
                    id: selectedProfitBoostItem.id,
                    eventId: GENERAL_FIELD_KEY
                  }),
                  setSelectedProfitBoostEventId(null)
                )
              );
            }

            if (getBetSlipFromLs().bets.length === 0) {
              toggleBetslipView(false);
            }

            calculateAndDispatchUpdates({
              id: GENERAL_FIELD_KEY,
              stake: ''
            });
            calculateAndDispatchSingleBetsUpdates('', Object.keys(stakes));
          }
        },
        (error: TSuperBetError) => {
          setInProgress(false);

          handleSuperBetError(
            getType(betslipBetType) as TSuperBetEventType,
            error
          );
        },
        setAcceptBet,
        document.getElementById('betslip-wrapper') || betslipRef.current
      );
    }
  };

  const ukDoBet = () => {
    const stakesCount = Object.keys(stakes.single).length;
    const isPartialBet = stakesCount > 0 && stakesCount < allLsBets.length;

    const stake =
      stakes.general ||
      Object.values(stakes.single).find(stake => Number(stake) > 0);

    if (
      (isPartialBet ||
        parseFloat(stake || '0') > 0 ||
        (totalSingleWin > 0 && totalStake > 0) ||
        (selectedFreeBetEventId &&
          selectedFreeBetEventId !== GENERAL_FIELD_KEY)) &&
      (advancedSelectedTab !== EAdvancedSelectedTabs.QUICKBET ||
        allLsBets.length === 1)
    ) {
      singleDoBet();
    }

    const mode = SpringConfigs.SUPER_BET_DEFAULT_ON
      ? -1
      : Number(
          JSON.parse(LS.getItem(storageKeyName('betslip', 'ACCEPT_BET'))).value
        );

    const bets = allLsBets.map(bet => {
      return {
        event_id: Number(bet.eventId),
        price: bet.sp ? BET_SP_COEFICIENT : Number(bet.coeficient),
        booking_id: bet.booking_id
      };
    });

    const data: {
      bets: Array<{
        event_id: number;
        price: number;
        booking_id?: number;
      }>;
      mode?: number;
      amount: number;
      type: number;
      source?: string;
      sys_bet?: number;
      each_way?: boolean;
      bonus_id?: number;
    } = {
      bets: bets,
      mode: mode,
      amount: 0,
      type: 2,
      sys_bet: 0,
      each_way: false
    };

    let updatedBetTypes = structuredClone(betTypesConf);

    if (advancedSelectedTab === EAdvancedSelectedTabs.QUICKBET) {
      updatedBetTypes = updatedBetTypes.filter(
        betItem =>
          betItem.count === allLsBets.length &&
          !betItem.strongCount &&
          betItem.type !== 1
      );
    }

    updatedBetTypes.map((item: UkBets) => {
      if (
        (item.amount > 0 && item.type !== 1) ||
        (selectedFreeBetEventId === GENERAL_FIELD_KEY && item.combCount === 1)
      ) {
        data.amount = item.each_way
          ? +item.amount * 2
          : selectedFreeBetEventId === GENERAL_FIELD_KEY
          ? selectedFreeBetAmount || 0
          : item.amount;
        data.sys_bet = Number(item.sys_bet);
        data.type = item.type;
        data.each_way = item.each_way;
        data.source = BETSLIP_DO_BET_CONSTANTS.ukBetSource;

        if (selectedFreeBetEventId === GENERAL_FIELD_KEY && selectedFreeBetId) {
          data.bonus_id = selectedFreeBetId;
        }

        if (
          item.count === allLsBets.length &&
          betslipType === EBetslipType.ADVANCED &&
          selectedProfitBoostItem?.boost_max_bet_amounts &&
          data.amount <=
            selectedProfitBoostItem?.boost_max_bet_amounts?.MaxBetAmount
        ) {
          data.bonus_id = selectedProfitBoostItem.id;
        }

        doBetHandler(
          data,
          null,
          (response: any) => {
            const { result } = response;
            setInProgress(false);

            if (result === 'OK') {
              const shouldRemoveBets =
                SpringConfigs.AFTER_SUCCESSFUL_BET ===
                EAfterSuccessfulBet.REMOVE_EVENTS;

              if (shouldRemoveBets) {
                removeActiveTabLoading();
              }

              setTimeout(() => {
                if (shouldRemoveBets) {
                  removeAllBetSlipEvent();
                }

                dispatch(triggerBetted());
                // modal success for displaying
              }, BET_SUCCESS_MODAL_DURATION);

              if (selectedProfitBoostItem) {
                dispatch(
                  batchActions(
                    removeProfitBoostItem({
                      id: selectedProfitBoostItem.id,
                      eventId: GENERAL_FIELD_KEY
                    }),
                    setSelectedProfitBoostEventId(null)
                  )
                );
              }

              clearUkTypes();
            }
          },
          (e: TDoBetErrorCallback) => {
            const betType = getIsMultipleGeneralField(item)
              ? EBetslipBetType.MULTIPLE
              : EBetslipBetType.SYSTEM;

            setInProgress(false);

            handleSuperBetError(betType, e);

            if (e.data?.result === ERROR_CODE_TIMEOUT_1800) {
              setAcceptBet(true);
            }
          },
          undefined,
          document.getElementById('betslip-wrapper') || betslipRef.current
        );
      }
    });
  };

  const clearUkTypes = useCallback(() => {
    let betTypesConfData: Array<UkBets> = [];
    betTypesConfData = JSON.parse(JSON.stringify(betTypesConf));
    betTypesConfData.map(betType => {
      betType.active = false;
      betType.stake = '';
      betType.amount = 0;
      betType.each_way = false;

      if (betType.name === 'Single') {
        betType.combCount = 1;
      }
    });

    dispatch(setBetUkType(betTypesConfData));
  }, [betTypesConf]);

  const onPlaceBet = () => {
    setAcceptBet(false);
    setInProgress(true);
    setShouldValidateRecaptchaV3(false);

    setSelected('');

    if (betslipType === 0) {
      switch (BET_TYPE_NAMES_KEYS[currentType]) {
        case BET_TYPE_NAMES_KEYS[EBetslipBetType.SINGLE]:
          singleDoBet();
          break;
        case BET_TYPE_NAMES_KEYS[EBetslipBetType.MULTIPLE]:
          multipleAndSystemDoBet(BET_TYPES.MULTIPLE);
          break;
        case BET_TYPE_NAMES_KEYS[EBetslipBetType.CHAIN]:
          multipleAndSystemDoBet(BET_TYPES.CHAIN);
          break;
        case BET_TYPE_NAMES_KEYS[EBetslipBetType.SYSTEM]:
          multipleAndSystemDoBet(BET_TYPES.SYSTEM);
      }
    } else {
      ukDoBet();
    }
  };

  return {
    clearUkTypes,
    onPlaceBet
  };
};

export const useDoBetHandler = (): {
  doBetHandler: (
    data: doBetProps,
    totalReturn: number | null,
    callback: Function,
    callbackError: Function | null,
    setAcceptBet?: Function,
    messageContext?: HTMLElement | null,
    showToastMessageOnSuccess?: boolean
  ) => void;
} => {
  const { checkActionAllowed } = UseCheckGeoComply();

  const doBetHandler = (
    data: doBetProps,
    possibleWin: number | null,
    callback: Function,
    callbackError: Function | null,
    setAcceptBet?: Function,
    messageContext?: HTMLElement | null,
    showToastMessageOnSuccess?: boolean
  ) => {
    if (SpringConfigs.GEO_COMPLY_ENABLED) {
      checkActionAllowed('do_bet')
        .then(geo_comply_token => {
          data.location_token = geo_comply_token as string;
          doBet({
            data,
            callback,
            additionalInfo: possibleWin ? { possibleWin } : {},
            callbackError,
            setAcceptBet,
            messageContext,
            showToastMessageOnSuccess
          });
        })
        .catch(e => {
          callbackError &&
            callbackError({
              data: {
                result: 0
              },
              StatusCode: '0'
            });
          console.info(e);
        });

      return;
    } else {
      doBet({
        data,
        callback,
        callbackError,
        setAcceptBet,
        messageContext,
        additionalInfo: possibleWin ? { possibleWin } : {},
        showToastMessageOnSuccess
      });
    }
  };

  return {
    doBetHandler
  };
};
